/**
 * useHints — global toggle for contextual field hints.
 *
 * State persists in localStorage (key `vanagreen:hints_enabled`).
 * Syncs across tabs via the `storage` event.
 */
import { useEffect, useState } from 'react';

const LS_KEY = 'vanagreen:hints_enabled';

function readHints(): boolean {
  try {
    return localStorage.getItem(LS_KEY) === 'true';
  } catch {
    return false;
  }
}

export function useHints(): { hintsEnabled: boolean; toggleHints: () => void } {
  const [hintsEnabled, setHintsEnabled] = useState<boolean>(readHints);

  // Cross-tab sync
  useEffect(() => {
    const handler = (e: StorageEvent): void => {
      if (e.key === LS_KEY) {
        setHintsEnabled(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const toggleHints = (): void => {
    setHintsEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LS_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return { hintsEnabled, toggleHints };
}
